import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { CloudinaryUploadResult } from '@interfaces/dws/cloudinary-info';
import {
  GalleryAssetMetadata,
  PromotedGalleryAsset,
  PendingGalleryAsset,
  tempFolder,
  storeFolder,
  toStoreDbPath,
  buildGalleryAssetPublicId,
  expectedStorePublicId,
} from '@interfaces/dws/gallery-asset';
import { IntegrationEntityTypes } from '@interfaces/integration-sync-metadata';
import { resource } from '@util/resource';
import {
  CARD_COVER_TRANSFORMS,
  DEFAULT_DELIVERY_TRANSFORMS,
} from '@util/cloudinary-url.util';

export interface DirectUploadOptions {
  cloudName: string;
  uploadPreset: string;
  wineryId: string;
  accountId: string;
  folder?: string;
  entityId?: string;
  entityType?: IntegrationEntityTypes;
}

export interface PromoteToStoreParams {
  file?: File | Blob;
  secureUrl?: string;
  tempPublicId?: string;
  metadata: GalleryAssetMetadata;
}

interface CloudinaryUploadSignature {
  cloudName: string;
  apiKey: string;
  timestamp: number;
  signature: string;
  publicId: string;
  uploadUrl: string;
}

@Injectable({
  providedIn: 'root'
})
export class CloudinaryUploadService {

  START_LINK = 'https://res.cloudinary.com/divinea/image/upload';
  CLOUDINARY_CLOUD_NAME = 'divinea';
  /** @deprecated Legacy unsigned preset — gallery uploads use backend-signed uploads instead */
  CLOUDINARY_UPLOAD_PRESET = 'wine-suite-temp';
  CLOUDINARY_UPLOAD_URL = `https://api.cloudinary.com/v1_1/${this.CLOUDINARY_CLOUD_NAME}/image/upload`;

  uploadImage(
    file: File | Blob,
    wineryId: string,
    imageName: string,
    entityId?: string,
    entityType?: IntegrationEntityTypes
  ): Observable<CloudinaryUploadResult> {
    return this.uploadToTemp(file, {
      wineryId,
      entityId,
      entityType,
      fileName: imageName,
    });
  }

  uploadImageBase64(
    base64: string,
    wineryId: string,
    imageName: string,
    entityId?: string,
    entityType?: IntegrationEntityTypes
  ): Observable<CloudinaryUploadResult> {
    const byteString = atob(base64.split(',')[1]);
    const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });
    return this.uploadToTemp(blob, {
      wineryId,
      entityId,
      entityType,
      fileName: imageName,
    });
  }

  uploadToTemp(
    file: File | Blob,
    metadata: GalleryAssetMetadata
  ): Observable<CloudinaryUploadResult> {
    const folder = tempFolder(metadata.wineryId);
    const publicId = buildGalleryAssetPublicId(folder, metadata.fileName);
    return this.signedUpload(file, publicId, metadata, folder);
  }

  promoteToStore(params: PromoteToStoreParams): Observable<PromotedGalleryAsset> {
    const tempPublicId = params.tempPublicId;
    if (!tempPublicId) {
      throw new Error('promoteToStore requires tempPublicId — store promote uses Cloudinary rename, not re-upload');
    }

    const wineryStorePrefix = `${storeFolder(params.metadata.wineryId)}/`;
    if (tempPublicId.startsWith(wineryStorePrefix)) {
      return new Observable((observer) => {
        observer.next({
          storePublicId: tempPublicId,
          storeRelativePath: toStoreDbPath(tempPublicId, params.metadata.wineryId),
          secureUrl: params.secureUrl || this.getImageUrl(tempPublicId),
          width: undefined,
          height: undefined,
        });
        observer.complete();
      });
    }

    const wineryStoreFolder = storeFolder(params.metadata.wineryId);

    return resource('crm://cloudinary/promote-to-store').post<CloudinaryUploadResult>({
      publicId: tempPublicId,
      wineryId: params.metadata.wineryId,
    }).pipe(
      map((data) => {
        const responsePublicId =
          data.public_id ??
          (data as { publicId?: string }).publicId ??
          expectedStorePublicId(tempPublicId, params.metadata.wineryId);
        if (!responsePublicId.startsWith(wineryStoreFolder + '/')) {
          throw new Error(
            `Promote did not place asset under ${wineryStoreFolder}/ — got "${responsePublicId}"`
          );
        }
        const storePublicId = responsePublicId;
        return {
          storePublicId,
          storeRelativePath: toStoreDbPath(storePublicId, params.metadata.wineryId),
          secureUrl: data.secure_url ?? data.url ?? this.getImageUrl(storePublicId),
          width: data.width,
          height: data.height,
        };
      })
    );
  }

  promoteAllToStore(
    pending: PendingGalleryAsset[],
    metadata: GalleryAssetMetadata
  ): Observable<PromotedGalleryAsset[]> {
    return new Observable((observer) => {
      const results: PromotedGalleryAsset[] = [];
      const run = async () => {
        for (const item of pending) {
          const itemMetadata: GalleryAssetMetadata = {
            ...metadata,
            fileName: item.name,
            displayOrder: item.displayOrder,
            flags: item.flags,
          };
          const promoted = await this.promoteToStore({
            file: item.file,
            secureUrl: item.tempSecureUrl,
            tempPublicId: item.tempPublicId,
            metadata: itemMetadata,
          }).toPromise();
          if (promoted) {
            results.push(promoted);
          }
        }
        observer.next(results);
        observer.complete();
      };
      run().catch((err) => observer.error(err));
    });
  }

  /**
   * Move file from temp folder to store folder.
   * Call this when the entity/form is saved after uploadImage() or uploadImageBase64().
   */
  moveToStore(publicId: string, wineryId: string): Observable<CloudinaryUploadResult> {
    const targetFolder = `production/store/${wineryId}`;
    return resource('crm://cloudinary/move')
      .params({ publicId, targetFolder })
      .post({});
  }

  /**
   * Delivery URL for UI display — always includes f_auto/q_auto (never raw original).
   */
  getImageUrl(
    publicId: string,
    transforms: string = DEFAULT_DELIVERY_TRANSFORMS
  ): string {
    return this.getImageUrlWithTransformation(publicId, transforms);
  }

  /** Optimized cover for experience / product cards in lists. */
  getCardCoverUrl(publicId: string): string {
    return this.getImageUrlWithTransformation(publicId, CARD_COVER_TRANSFORMS);
  }

  /**
   * Raw upload URL without transforms. Prefer getImageUrl / getCardCoverUrl for UI.
   */
  getRawImageUrl(publicId: string): string {
    return `${this.START_LINK}/v1/${publicId}`;
  }

  getPreviewUrl(response: CloudinaryUploadResult): string {
    return response.secure_url || response.url;
  }

  getImageUrlWithTransformation(publicId: string, transformation: string): string {
    return `${this.START_LINK}/${transformation}/v1/${publicId}`;
  }

  getImageUrlWithTransformationAndSize(
    publicId: string,
    transformation: string,
    width: number,
    height: number
  ): string {
    return `${this.START_LINK}/${transformation}/w_${width},h_${height},c_fill/v1/${publicId}`;
  }

  getImageUrlWithTransformationAndSizeAndQuality(
    publicId: string,
    transformation: string,
    width: number,
    height: number,
    quality: number
  ): string {
    return `${this.START_LINK}/${transformation}/w_${width},h_${height},q_${quality},c_fill/v1/${publicId}`;
  }

  uploadImageDirect(
    file: File | Blob,
    imageName: string,
    options: DirectUploadOptions,
    onProgress?: (progress: number) => void
  ): Observable<CloudinaryUploadResult> {
    return new Observable((observer) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', options.uploadPreset);
      const folder = options.folder || storeFolder(options.wineryId);
      formData.append('folder', folder);

      const metadata: GalleryAssetMetadata = {
        wineryId: options.wineryId,
        entityId: options.entityId,
        entityType: options.entityType,
        fileName: imageName,
      };
      this.appendContext(formData, metadata);

      const timestamp = Date.now();
      const sanitizedName = imageName.replace(/[^a-zA-Z0-9._-]/g, '_');
      formData.append('public_id', `${folder}/${timestamp}_${sanitizedName}`);

      const uploadUrl = `https://api.cloudinary.com/v1_1/${options.cloudName}/image/upload`;
      const xhr = new XMLHttpRequest();

      if (onProgress) {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            onProgress((e.loaded / e.total) * 100);
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status === 200) {
          try {
            observer.next(JSON.parse(xhr.responseText));
            observer.complete();
          } catch {
            observer.error(new Error('Failed to parse Cloudinary response'));
          }
        } else {
          try {
            const err = JSON.parse(xhr.responseText);
            observer.error(new Error(err.error?.message || 'Upload failed'));
          } catch {
            observer.error(new Error(`Upload failed with status ${xhr.status}`));
          }
        }
      });

      xhr.addEventListener('error', () => observer.error(new Error('Network error during upload')));
      xhr.addEventListener('abort', () => observer.error(new Error('Upload aborted')));
      xhr.open('POST', uploadUrl);
      xhr.send(formData);
    });
  }

  private signedUpload(
    file: File | Blob,
    publicId: string,
    metadata: GalleryAssetMetadata,
    expectedPathPrefix: string
  ): Observable<CloudinaryUploadResult> {
    return this.requestUploadSignature(publicId, metadata).pipe(
      switchMap((signature) => {
        const formData = new FormData();
        formData.append('file', file);
        this.appendSignedFields(formData, signature, metadata);
        return this.postUpload(formData, signature.uploadUrl, expectedPathPrefix);
      })
    );
  }

  private requestUploadSignature(
    publicId: string,
    metadata: GalleryAssetMetadata
  ): Observable<CloudinaryUploadSignature> {
    const context = this.buildContextString(metadata);
    return resource('crm://cloudinary/upload-signature').post<CloudinaryUploadSignature>({
      publicId,
      context: context || undefined,
      wineryId: metadata.wineryId,
    });
  }

  private appendSignedFields(
    formData: FormData,
    signature: CloudinaryUploadSignature,
    metadata: GalleryAssetMetadata
  ): void {
    formData.append('api_key', signature.apiKey);
    formData.append('timestamp', String(signature.timestamp));
    formData.append('signature', signature.signature);
    formData.append('public_id', signature.publicId);
    const context = this.buildContextString(metadata);
    if (context) {
      formData.append('context', context);
    }
  }

  private buildContextString(metadata: GalleryAssetMetadata): string {
    const parts: string[] = [];
    if (metadata.wineryId) {
      parts.push(`winery_id=${metadata.wineryId}`);
    }
    if (metadata.entityId) {
      parts.push(`entity_id=${metadata.entityId}`);
    }
    if (metadata.entityType) {
      parts.push(`entity_type=${metadata.entityType}`);
    }
    if (metadata.fileName) {
      parts.push(`file_name=${metadata.fileName}`);
    }
    if (metadata.displayOrder !== undefined) {
      parts.push(`display_order=${metadata.displayOrder}`);
    }
    if (metadata.flags?.length) {
      parts.push(`flags=${JSON.stringify(metadata.flags)}`);
    }
    return parts.join('|');
  }

  private assertUploadPath(publicId: string, expectedPrefix: string): void {
    if (!publicId?.startsWith(expectedPrefix)) {
      throw new Error(
        `Cloudinary saved the file as "${publicId}" but expected path under "${expectedPrefix}/".`
      );
    }
  }

  private appendContext(formData: FormData, metadata: GalleryAssetMetadata): void {
    const context = this.buildContextString(metadata);
    if (context) {
      formData.append('context', context);
    }
  }

  private postUpload(
    formData: FormData,
    uploadUrl: string,
    expectedPathPrefix?: string
  ): Observable<CloudinaryUploadResult> {
    return from(
      fetch(uploadUrl, { method: 'POST', body: formData }).then(async (response) => {
        if (!response.ok) {
          const body = await response.text();
          throw new Error(`Upload failed: ${response.statusText}${body ? ` — ${body}` : ''}`);
        }
        const data = (await response.json()) as CloudinaryUploadResult;
        if (expectedPathPrefix) {
          this.assertUploadPath(data.public_id, expectedPathPrefix);
        }
        return data;
      })
    );
  }
}
